import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import { ClassifyPurchaseDialog } from './ClassifyPurchaseDialog';

const STATUS = { pending: ['Pending', 'bg-slate-100 text-slate-700'], missing_info: ['Missing info', 'bg-amber-100 text-amber-800'], reviewed: ['Reviewed', 'bg-emerald-100 text-emerald-700'] };

const TrialRun = ({ data }) => (
  <div className="rounded-lg border bg-muted/40 p-3 text-xs space-y-1" data-testid="vat-trial-run">
    <div className="font-semibold">Read-only trial run · nothing posted · fingerprint {data.fingerprint}</div>
    <div>{data.eligible_lines} reviewed standard-rated purchases · gross {data.total_gross.toFixed(2)} · proposed input VAT <b>{data.total_proposed_vat.toFixed(2)}</b></div>
    <div className="text-muted-foreground">{data.unchanged} Posting needs separate owner approval.</div>
  </div>
);

export const PurchaseVatReviewDialog = ({ open, onClose }) => {
  const [status, setStatus] = useState('');
  const [data, setData] = useState({ items: [], counts: {} });
  const [sel, setSel] = useState(null);
  const [trial, setTrial] = useState(null);
  const load = () => api.get('/purchase-vat-review', { params: { month: '2026-10', status: status || undefined } })
    .then((r) => setData(r.data)).catch((e) => toast.error(e?.response?.data?.detail || 'Could not load review'));
  useEffect(() => { if (open) load(); }, [open, status]); // eslint-disable-line react-hooks/exhaustive-deps
  const runTrial = () => api.get('/purchase-vat-review/trial-run', { params: { month: '2026-10' } }).then((r) => setTrial(r.data));
  const c = data.counts || {};
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="purchase-vat-review-dialog">
        <DialogHeader><DialogTitle>October 2026 purchase VAT review — classify without posting</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {[['', `All ${data.total ?? ''}`], ['pending', `Pending ${c.pending ?? 0}`], ['missing_info', `Missing info ${c.missing_info ?? 0}`], ['reviewed', `Reviewed ${c.reviewed ?? 0}`]].map(([k, l]) => (
            <Button key={k || 'all'} size="sm" variant={status === k ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => setStatus(k)} data-testid={`vat-review-filter-${k || 'all'}`}>{l}</Button>
          ))}
          <Button size="sm" variant="outline" className="h-7 text-xs ml-auto" onClick={runTrial} data-testid="vat-review-trial-btn">Read-only trial run</Button>
        </div>
        {trial && <TrialRun data={trial} />}
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="vat-review-table">
            <thead><tr className="border-b text-muted-foreground"><th className="text-left p-2">Date</th><th className="text-left p-2">Ref</th><th className="text-left p-2">Branch</th>
              <th className="text-left p-2">Supplier / category</th><th className="text-right p-2">Gross</th><th className="text-left p-2">Status</th><th className="p-2" /></tr></thead>
            <tbody>
              {data.items.slice(0, 300).map((i) => (
                <tr key={i.id} className="border-b hover:bg-muted/40" data-testid={`vat-review-row-${i.id}`}>
                  <td className="p-2 font-mono">{i.date}</td><td className="p-2 font-mono">{i.ref}</td><td className="p-2">{i.branch}</td>
                  <td className="p-2">{i.supplier || '—'}<div className="text-muted-foreground">{i.category}{i.review.vat_treatment ? ` · ${i.review.vat_treatment.replace('_', ' ')}` : ''}</div></td>
                  <td className="p-2 text-right tabular-nums">{i.gross.toFixed(2)}</td>
                  <td className="p-2"><Badge className={`text-[10px] ${STATUS[i.status][1]}`}>{STATUS[i.status][0]}</Badge>
                    {i.missing.length > 0 && <div className="text-[10px] text-amber-700">needs {i.missing.join(', ')}</div>}</td>
                  <td className="p-2 text-right"><Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setSel(i)} data-testid={`vat-review-classify-${i.id}`}>Classify</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.items.length > 300 && <p className="text-[11px] text-muted-foreground p-2">Showing first 300 — use the status filters to work through the rest.</p>}
        </div>
        <ClassifyPurchaseDialog item={sel} onClose={() => setSel(null)} onSaved={() => { setSel(null); load(); setTrial(null); }} />
      </DialogContent>
    </Dialog>
  );
};
